import React from 'react';
import QuoteForm from '../components/QuoteForm';
import { MessageSquare, Clock, DollarSign, CheckCircle, Phone, Star, Shield } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { businessInfo } from '../data/mock';

const QuotePage = () => {
  const benefits = [
    {
      icon: DollarSign,
      title: "100% Free - No Obligation",
      description: "Get a detailed quote with no commitment required. No hidden fees or surprise charges."
    },
    {
      icon: Clock,
      title: "Fast Response Time",
      description: "We respond to all quote requests within 24 hours. Need it faster? Call us directly."
    },
    {
      icon: CheckCircle,
      title: "Expert Recommendations",
      description: "Get professional advice tailored to your specific needs and budget."
    },
    {
      icon: Shield,
      title: "Transparent Pricing",
      description: "Clear, itemized quotes so you know exactly what you're paying for."
    }
  ];

  const process = [
    {
      step: "1",
      title: "Submit Your Request",
      description: "Fill out our simple form with details about your garage door needs. Takes less than 2 minutes."
    },
    {
      step: "2",
      title: "We Review & Contact You",
      description: "Our team reviews your request and contacts you within 24 hours to discuss details and schedule an assessment if needed."
    },
    {
      step: "3",
      title: "Receive Your Quote",
      description: "Get a detailed, transparent quote including all costs, timeline, and product options."
    },
    {
      step: "4",
      title: "Approve & Schedule",
      description: "Once you approve, we'll schedule the work at a time convenient for you. No pressure, no rush."
    }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      location: "Regina, SK",
      rating: 5,
      text: "Got a quote within hours and the price was very competitive. Installation was perfect!",
      service: "New Door Installation"
    },
    {
      name: "Mike Thompson",
      location: "Saskatoon, SK",
      rating: 5,
      text: "Clear, detailed quote with no surprises. The work was completed exactly as quoted.",
      service: "Door Repair"
    },
    {
      name: "Jennifer Lee",
      location: "Moose Jaw, SK",
      rating: 5,
      text: "Very transparent pricing and excellent communication throughout the process.",
      service: "Spring Replacement"
    }
  ];

  const commonServices = [
    {
      title: "New Door Installation",
      features: ["Free consultation", "Professional installation", "Warranty included"]
    },
    {
      title: "Door Repair",
      features: ["Same-day service", "All brands", "90-day warranty"]
    },
    {
      title: "Opener Installation",
      features: ["Smart options", "Wi-Fi setup", "Programming included"]
    },
    {
      title: "Spring Replacement",
      features: ["Emergency available", "High-cycle springs", "Balance adjustment"]
    },
    {
      title: "Maintenance Service",
      features: ["20-point inspection", "Lubrication", "Safety test"]
    },
    {
      title: "Panel Replacement",
      features: ["Color matching", "Professional install", "Weather seal"]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Hero */}
      <section className="pt-32 pb-12 bg-gradient-to-r from-blue-900 to-blue-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <MessageSquare size={64} className="mx-auto mb-6" />
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Get Your Free Quote Today
            </h1>
            <p className="text-xl text-blue-100 mb-4">
              No obligation • Fast response • Competitive pricing
            </p>
            <div className="flex flex-wrap justify-center gap-2 text-blue-200">
              <span className="flex items-center gap-1">
                <CheckCircle size={18} />
                Free Consultation
              </span>
              <span>•</span>
              <span className="flex items-center gap-1">
                <CheckCircle size={18} />
                24-Hour Response
              </span>
              <span>•</span>
              <span className="flex items-center gap-1">
                <CheckCircle size={18} />
                Transparent Pricing
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Request a Quote From Us?</h2>
            <p className="text-xl text-gray-600">We make the process easy and transparent</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto mb-16">
            {benefits.map((benefit, idx) => {
              const IconComponent = benefit.icon;
              return (
                <Card key={idx} className="border-2 border-gray-200 hover:border-blue-600 text-center transition-all">
                  <CardHeader>
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent size={32} className="text-blue-700" />
                    </div>
                    <CardTitle className="text-lg mb-2">{benefit.title}</CardTitle>
                    <CardDescription className="text-base">{benefit.description}</CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <QuoteForm />

      {/* Common Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Services We Offer</h2>
            <p className="text-xl text-gray-600">Request a quote for any of our professional services</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {commonServices.map((service, idx) => (
              <Card key={idx} className="border-2 hover:border-blue-600 hover:shadow-xl transition-all">
                <CardHeader>
                  <CardTitle className="text-xl text-gray-900 mb-4">{service.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, fidx) => (
                      <li key={fidx} className="flex items-start gap-2 text-gray-700">
                        <CheckCircle size={18} className="text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => window.location.href = '/quote'}
                      size="sm"
                      className="bg-blue-800 hover:bg-blue-900 text-white flex-1 font-bold"
                    >
                      Get Quote
                    </Button>
                    <Button 
                      onClick={() => window.location.href = `tel:${businessInfo.phone}`}
                      size="sm"
                      variant="outline"
                      className="border-2 border-green-600 text-green-700 hover:bg-green-600 hover:text-white flex-1 font-bold"
                    >
                      Call
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-xl text-gray-600">Our simple 4-step process</p>
          </div>
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {process.map((item, idx) => (
                <div key={idx} className="flex gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-blue-800 text-white rounded-full flex items-center justify-center text-2xl font-bold shadow-lg">
                      {item.step}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Our Customers Say</h2>
            <p className="text-xl text-gray-600">Real reviews from satisfied clients</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {testimonials.map((testimonial, idx) => (
              <Card key={idx} className="border-2">
                <CardHeader>
                  <div className="flex gap-1 mb-2">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={20} className="fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <CardTitle className="text-lg">{testimonial.name}</CardTitle>
                  <CardDescription>{testimonial.location}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-3 italic">"{testimonial.text}"</p>
                  <div className="text-sm text-blue-700 font-semibold">{testimonial.service}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Need Help CTA */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-green-700">
        <div className="container mx-auto px-4 text-center text-white">
          <Phone size={64} className="mx-auto mb-6" />
          <h2 className="text-4xl font-bold mb-4">Prefer to Talk Directly?</h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Our friendly team is ready to answer your questions and provide immediate quotes over the phone.
          </p>
          <Button 
            onClick={() => window.location.href = `tel:${businessInfo.phone}`}
            size="lg"
            className="bg-white text-green-700 hover:bg-gray-100 font-bold px-12 py-8 text-xl"
          >
            <Phone size={24} className="mr-2" />
            Call {businessInfo.phone}
          </Button>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-blue-800">
        <div className="container mx-auto px-4 text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Ready to Get Your Free Quote?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Fill out the form above or call us now. We're here to help with all your garage door needs.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              size="lg"
              className="bg-green-600 hover:bg-green-700 text-white font-bold px-12 py-8 text-lg"
            >
              <MessageSquare size={24} className="mr-2" />
              Request Quote Now
            </Button>
            <Button 
              onClick={() => window.location.href = '/products'}
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-blue-900 font-bold px-12 py-8 text-lg"
            >
              View Products
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default QuotePage;